import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  getClubTeams,
  getMySessions,
  getPointsSummary,
  type PointsSummary,
} from "../../services/api";
import { getAssignedQuizzes, AssignedQuiz, getMyQuizCategories, type MyQuizCategory } from "../../services/quizzes";
import { getAthleteThisWeekDrops, type AthleteScheduledQuiz } from "../../services/quizSchedules";
import { listMyPrizes, type PeriodicPrize } from "../../services/periodicPrizes";
import { getMyBadgeProgress, type BadgeProgress } from "../../services/badges";
import { useAuth } from "../../context/AuthContext";
import { isIndividualClub } from "../../constants/individualClub";
import MobileAppLayout from "../../layouts/MobileAppLayout";
import api from "../../services/api";
import { GameCard, GameIcon, RingProgress, StatTile, Skeleton } from "../../components/game";
import { formatDate } from "../../lib/dateFormat";
import { useSportNames } from "../../components/SportChips";

// Minimal shape of a club-leaderboard row for the dashboard peek.
type LeaderRow = {
  rank: number;
  user_id: string;
  full_name: string;
  avatar_url: string | null;
  points: number;
};

type AthleteProduct = {
  id: string;
  product_id: string;
  due_day: number | null;
  next_due_date: string | null;
  payment_status: "pending" | "paid" | "overdue";
  payment_method: string | null;
  notes: string | null;
  product: {
    id: string;
    name: string;
    description: string | null;
    price_cents: number;
    currency: string;
    recurrence_type: string;
  };
};

type MyProductsResponse = {
  products: AthleteProduct[];
  club: {
    name: string;
    payment_methods: string[] | null;
  } | null;
  /** True for an adult athlete with no parent on file — they pay their own. */
  payer_is_self?: boolean;
};

export default function AppDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, clubs: ctxClubs, activeClubId: ctxClubId, roles } = useAuth();
  const sportName = useSportNames();

  const [clubId, setClubId] = useState<string | null>(ctxClubId);
  const [teams, setTeams] = useState<{ id: string; name: string; age_group?: string | null; sport_type?: string | null; coach_id?: string | null }[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [assignedQuizzes, setAssignedQuizzes] = useState<AssignedQuiz[]>([]);
  // Schedule-based drops for the current week. Surfaced alongside the
  // legacy assigned-quiz list so the dashboard count + pending list
  // include both sources.
  const [scheduledDrops, setScheduledDrops] = useState<AthleteScheduledQuiz[]>([]);
  // Distinct quizzes the athlete has completed across ALL categories — the
  // authoritative "quizzes done" total (assigned/weekly-drop sources miss
  // quizzes finished by browsing categories directly).
  const [categoriesCompleted, setCategoriesCompleted] = useState(0);
  const [pointsSummary, setPointsSummary] = useState<PointsSummary | null>(null);
  const [recentPrize, setRecentPrize] = useState<PeriodicPrize | null>(null);
  const [myProducts, setMyProducts] = useState<AthleteProduct[]>([]);
  // A child sees a reminder to tell their parent; an adult athlete who pays
  // their own fees sees the amounts and a way to settle them.
  const [payerIsSelf, setPayerIsSelf] = useState(false);
  const [clubPaymentMethods, setClubPaymentMethods] = useState<string[]>([]);
  const [badges, setBadges] = useState<BadgeProgress[]>([]);
  const [leaders, setLeaders] = useState<LeaderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Gates the initial skeleton — flips true once the primary user data
  // (points/level, quizzes, badges) has resolved, success or failure.
  const [userDataLoaded, setUserDataLoaded] = useState(false);

  // Get current club
  const activeClubId = ctxClubId || ctxClubs[0]?.id;
  const currentClub = ctxClubs.find(c => c.id === activeClubId) || ctxClubs[0];
  const individual = isIndividualClub(currentClub?.id);

  // Sync club from context
  useEffect(() => {
    if (ctxClubId) setClubId(ctxClubId);
    else if (ctxClubs.length > 0) setClubId(ctxClubs[0].id);
  }, [ctxClubId, ctxClubs]);

  // Load teams and data
  useEffect(() => {
    const loadData = async () => {
      if (!clubId) return;
      setLoading(true);
      setError(false);
      try {
        const ts = await getClubTeams(clubId);
        setTeams(ts);

        // Upcoming sessions across the athlete's OWN team(s) — the backend
        // auto-scopes /sessions to the teams they belong to, so this no longer
        // picks an arbitrary club team.
        const ss = await getMySessions();
        const future = ss.filter((s) => new Date(s.start_at) > new Date());
        setSessions(future as any);
      } catch (error) {
        console.error("Failed to load data:", error);
        setError(true);
      }
      setLoading(false);
    };
    loadData();
  }, [clubId]);

  // Load quizzes, points, and products
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const [quizzes, summary, dropsResp, prizes, badgeProgress, categories] = await Promise.all([
          getAssignedQuizzes(),
          getPointsSummary(),
          getAthleteThisWeekDrops().catch((e) => {
            console.error("Failed to load scheduled drops:", e);
            return null;
          }),
          listMyPrizes().catch(() => [] as PeriodicPrize[]),
          getMyBadgeProgress().catch(() => [] as BadgeProgress[]),
          getMyQuizCategories().catch(() => [] as MyQuizCategory[]),
        ]);
        setAssignedQuizzes(quizzes);
        setPointsSummary(summary);
        setScheduledDrops(dropsResp?.drops || []);
        setCategoriesCompleted(categories.reduce((s, c) => s + (c.completed || 0), 0));
        setBadges(badgeProgress);
        // Most recent prize, if awarded in the last 7 days — banner
        // is only fresh news. Older wins are visible in the profile.
        if (prizes.length > 0) {
          const latest = prizes[0];
          const awardedAt = new Date(latest.awarded_at).getTime();
          if (Date.now() - awardedAt < 7 * 24 * 60 * 60 * 1000) {
            setRecentPrize(latest);
          }
        }

        // Load my products
        try {
          const { data } = await api.get<MyProductsResponse>("/products/my-products");
          setMyProducts(data.products);
          setClubPaymentMethods(data.club?.payment_methods || []);
          setPayerIsSelf(data.payer_is_self === true);
        } catch {
          // Not an athlete or no products
        }
      } catch (error) {
        console.error("Failed to load user data:", error);
      } finally {
        setUserDataLoaded(true);
      }
    };
    loadUserData();
  }, []);

  // Club leaderboard peek (weekly) — top of the club + my row.
  useEffect(() => {
    const loadLeaders = async () => {
      const cid = ctxClubId || ctxClubs[0]?.id;
      if (!cid) return;
      try {
        const params = new URLSearchParams({ period: "weekly", scope: "club", clubId: cid, limit: "50" });
        const { data } = await api.get(`/leaderboard?${params.toString()}`);
        setLeaders((data.entries || []) as LeaderRow[]);
      } catch {
        // leaderboard peek is best-effort
      }
    };
    loadLeaders();
  }, [ctxClubId, ctxClubs]);

  // Count COMPLETED quizzes across both sources (legacy age-group
  // assignments + this-week scheduled drops), dedup by quiz_id so a
  // quiz that appears in both doesn't double-count. Previously we
  // filtered on `user_attempt_count > 0` which included abandoned
  // attempts — an athlete who tapped in and bailed showed up as
  // Completate: 1. Now we use `user_completed_count` (attempts with
  // completed_at IS NOT NULL) and the schedule-drop `status`.
  const completedQuizIds = useMemo(() => {
    const ids = new Set<string>();
    for (const { quiz } of assignedQuizzes) {
      if (((quiz as any).user_completed_count ?? 0) > 0) ids.add(quiz.id);
    }
    for (const d of scheduledDrops) {
      if (d.status === "completed") ids.add(d.quiz.id);
    }
    return ids;
  }, [assignedQuizzes, scheduledDrops]);
  // Prefer the category-wide distinct-completed total (covers quizzes finished
  // by browsing categories, which the assigned/weekly-drop sets miss); fall
  // back to the assigned/drop set if categories didn't load.
  const completedQuizzes = Math.max(categoriesCompleted, completedQuizIds.size);
  const pendingQuizzes = assignedQuizzes.filter(
    ({ quiz }) => ((quiz as any).user_completed_count ?? 0) === 0,
  );

  // Total quizzes available to take this week = legacy pending +
  // scheduled drops, deduplicated by quiz id so a quiz appearing in
  // both sources doesn't double-count.
  const availableQuizCount = useMemo(() => {
    const ids = new Set<string>();
    pendingQuizzes.forEach(({ quiz }) => ids.add(quiz.id));
    scheduledDrops.forEach((d) => ids.add(d.quiz.id));
    return ids.size;
  }, [pendingQuizzes, scheduledDrops]);

  // Restrict the "My Info" widgets to teams the athlete is actually on.
  // Until this filter the athlete saw every team + sport in their club, which
  // looked like "you're on 8 teams" right after onboarding into one team.
  const myTeamIds = new Set(
    roles
      .filter(r => r.scope_type === "team" && r.scope_id)
      .map(r => r.scope_id as string)
  );
  const myTeams = myTeamIds.size > 0
    ? teams.filter(t => myTeamIds.has(t.id))
    : teams; // fallback for users without team-scoped roles (e.g. parent on athlete view)

  // Get unique sports from MY teams only
  // `sport_type` is a name on new teams and an id on older ones; resolve
  // before de-duplicating so the same sport does not appear twice.
  const sports = Array.from(
    new Set(myTeams.map(t => t.sport_type).filter((s): s is string => !!s).map(sportName))
  );

  // Get next session
  const nextSession = sessions[0];

  // Level / XP for the ring hero
  const lvl = pointsSummary?.level;
  const xpToNext = lvl ? Math.max(0, lvl.xp_for_next - lvl.xp_into_level) : 0;

  // Earned badges, most recent first — count for the stat + strip preview.
  const earnedBadges = useMemo(
    () =>
      badges
        .filter((b) => b.earned)
        .sort((a, b) => (b.earned_at || "").localeCompare(a.earned_at || "")),
    [badges],
  );
  // If the athlete hasn't earned any yet, preview the closest-to-unlock
  // ones so the strip is never empty.
  const badgeStrip = earnedBadges.length > 0 ? earnedBadges.slice(0, 6) : badges.slice(0, 6);

  // Featured quiz — first pending assignment (falls back to nothing).
  const featuredQuiz = pendingQuizzes[0]?.quiz;

  // Leaderboard peek — top two + my own row (deduped).
  const myLeaderRow = leaders.find((e) => e.user_id === user?.id);
  const leaderPeek = (() => {
    const top = leaders.slice(0, 2);
    if (myLeaderRow && !top.some((e) => e.user_id === myLeaderRow.user_id)) {
      return [...top, myLeaderRow];
    }
    return top;
  })();

  const getInitials = (name?: string | null) =>
    (name || "U")
      .split(" ")
      .map((p) => p[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();

  // Skeleton while the primary data loads — reserves the real layout so
  // the dashboard fades in instead of flashing empty then popping.
  if (!userDataLoaded) {
    return (
      <MobileAppLayout>
        <div className="px-4 py-6 space-y-6">
          {/* level hero */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_3px_0_#eef2f8] p-5 flex items-center gap-5">
            <Skeleton className="w-24 h-24 rounded-full shrink-0" />
            <div className="flex-1 space-y-2.5">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-40" />
              <Skeleton className="h-2 w-full mt-2" />
              <Skeleton className="h-2.5 w-16" />
            </div>
          </div>
          {/* stats */}
          <div className="grid grid-cols-3 gap-2.5">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-[76px]" />
            ))}
          </div>
          {/* featured quiz */}
          <div className="space-y-2.5">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-40 rounded-2xl" />
          </div>
          {/* next session */}
          <Skeleton className="h-[72px] rounded-2xl" />
          {/* badges strip */}
          <div className="flex gap-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="w-14 h-14 rounded-full" />
            ))}
          </div>
        </div>
      </MobileAppLayout>
    );
  }

  // A failed primary fetch used to render an empty dashboard indistinguishable
  // from "no data yet" — surface a visible error + retry instead.
  if (error) {
    return (
      <MobileAppLayout>
        <div className="px-4 py-6">
          <GameCard className="p-6 text-center">
            <h3 className="text-base font-bold text-slate-800 mb-1">{t("common.loadErrorTitle", "Ceva n-a mers")}</h3>
            <p className="text-sm text-slate-500">
              {t("common.loadError", "Nu am putut încărca datele. Verifică conexiunea și încearcă din nou.")}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white"
            >
              {t("common.retry", "Reîncearcă")}
            </button>
          </GameCard>
        </div>
      </MobileAppLayout>
    );
  }

  return (
    <MobileAppLayout>
      <div className="px-4 py-6 space-y-6">
        {/* Level hero — the athlete's level is the star of the dashboard */}
        <GameCard depth className="relative overflow-hidden p-5 flex items-center gap-5">
          <div className="pointer-events-none absolute -top-12 -right-10 w-44 h-44 rounded-full bg-indigo-100/70" />
          <RingProgress
            pct={lvl?.progress_pct ?? 0}
            size={96}
            stroke={9}
            color="#6366f1"
            track="#e9edf6"
            className="shrink-0"
          >
            <div className="flex flex-col items-center leading-none">
              <span className="text-[9px] font-extrabold tracking-[0.14em] text-slate-400">NIVEL</span>
              <span className="text-3xl font-black text-slate-800">{lvl?.level ?? 1}</span>
            </div>
          </RingProgress>
          <div className="relative flex-1 min-w-0">
            <div className="text-lg font-extrabold text-slate-800">{lvl?.title || t("dashboard.athlete", "Sportiv")}</div>
            <div className="text-sm text-slate-500 mt-0.5">
              {lvl
                ? `Mai ai ${xpToNext} XP până la Nivelul ${lvl.level + 1}`
                : t("dashboard.earnToLevel", "Adună puncte pentru a urca în nivel")}
            </div>
            <div className="mt-3">
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all duration-1000"
                  style={{ width: `${lvl?.progress_pct ?? 0}%` }}
                />
              </div>
              <div className="text-[11px] font-bold text-slate-400 mt-1.5">
                {lvl ? `${lvl.xp_into_level} / ${lvl.xp_for_next} XP` : "0 XP"}
              </div>
            </div>
          </div>
        </GameCard>

        {/* F4: recent prize banner — only shown when we have a prize
            awarded within the last week. Gradient card with medal +
            copy tuned to the rank. */}
        {recentPrize && (
          <div className="bg-gradient-to-r from-amber-400 via-orange-500 to-pink-500 rounded-2xl p-4 text-white shadow-lg flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-2xl flex-shrink-0">
              {recentPrize.rank === 1 ? "🥇" : recentPrize.rank === 2 ? "🥈" : "🥉"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm truncate">
                {recentPrize.scope === "platform"
                  ? `Locul ${recentPrize.rank} pe platformă`
                  : `Locul ${recentPrize.rank} pe club`}
              </p>
              <p className="text-xs text-white/90">
                {recentPrize.period_type === "week" ? "săptămâna trecută" : "luna trecută"} · +{recentPrize.points_awarded} puncte
              </p>
            </div>
          </div>
        )}

        {/* Interactive quiz challenge banner — the playful hook that
            pulls the athlete straight into the quizzes. */}
        {availableQuizCount > 0 && (
          <button
            onClick={() => navigate("/app/quizzes")}
            className="relative w-full overflow-hidden rounded-3xl p-5 text-left bg-gradient-to-br from-indigo-500 via-violet-500 to-fuchsia-500 text-white shadow-[0_10px_28px_-10px_rgba(139,92,246,0.7)] active:translate-y-[2px] transition-transform"
          >
            <div className="pointer-events-none absolute -top-8 -right-6 w-32 h-32 rounded-full bg-white/15 blur-xl" />
            <div className="pointer-events-none absolute top-3 right-5 text-xl animate-bounce">✨</div>
            <div className="relative flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/20 grid place-items-center text-3xl shrink-0 animate-pulse">🧠</div>
              <div className="flex-1 min-w-0">
                <div className="text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-white/80">
                  {t("dashboard.challenge", "Provocare")}
                </div>
                <div className="text-lg font-extrabold leading-tight">
                  {availableQuizCount} {availableQuizCount === 1 ? t("dashboard.quizWaitingOne", "quiz nou te așteaptă!") : t("dashboard.quizWaitingMany", "quiz-uri noi te așteaptă!")}
                </div>
                <div className="text-xs text-white/80 mt-0.5">
                  {t("dashboard.quizWaitingSub", "Răspunde rapid și câștigă puncte + insigne")}
                </div>
              </div>
            </div>
            <span className="relative mt-4 inline-flex items-center gap-2 bg-white text-indigo-700 font-extrabold text-sm px-4 py-2.5 rounded-2xl shadow-[0_4px_0_rgba(79,70,229,0.25)]">
              {t("dashboard.playNow", "Joacă acum")} <GameIcon name="bolt" className="w-4 h-4" />
            </span>
          </button>
        )}

        {/* Statistici */}
        <div>
          <div className="flex items-center justify-between mb-2.5 px-0.5">
            <h3 className="text-xs font-extrabold uppercase tracking-wide text-slate-400">{t("dashboard.stats", "Statistici")}</h3>
          </div>
          <div className="grid grid-cols-3 gap-2.5">
            <StatTile icon="star" color="#f59e0b" label={t("athlete.totalPoints", "Puncte")} value={(pointsSummary?.total ?? 0).toLocaleString("ro-RO")} onClick={() => navigate("/app/points")} />
            <StatTile icon="check" color="#10b981" label={t("athlete.quizzes", "Quiz-uri")} value={completedQuizzes} onClick={() => navigate("/app/quizzes")} />
            <StatTile icon="medal" color="#a78bfa" label={t("dashboard.badges", "Insigne")} value={earnedBadges.length} onClick={() => navigate("/app/badges")} />
          </div>
        </div>

        {/* Featured quiz */}
        {featuredQuiz && (
          <div>
            <div className="flex items-center justify-between mb-2.5 px-0.5">
              <h3 className="text-xs font-extrabold uppercase tracking-wide text-slate-400">{t("nav.quizzes", "Quiz-uri")}</h3>
              <Link to="/app/quizzes" className="text-xs font-bold text-indigo-600">{t("common.viewAll", "Vezi toate")}</Link>
            </div>
            <GameCard depth onClick={() => navigate(`/quiz/${featuredQuiz.id}`)} className="relative overflow-hidden p-5 cursor-pointer">
              <div className="pointer-events-none absolute -top-10 -right-8 w-40 h-40 rounded-full bg-indigo-50" />
              {availableQuizCount > 0 && (
                <span className="absolute top-4 right-4 text-[10px] font-black text-emerald-800 bg-emerald-300 rounded-full px-2 py-0.5">
                  {availableQuizCount} {availableQuizCount === 1 ? t("dashboard.oneNew", "nou") : t("dashboard.manyNew", "noi")}
                </span>
              )}
              <div className="relative text-[10.5px] font-extrabold tracking-[0.1em] uppercase text-indigo-500">{t("quizzes.weeklyQuiz", "Quiz-ul săptămânii")}</div>
              <div className="relative text-lg font-extrabold text-slate-800 mt-1">{featuredQuiz.title}</div>
              <div className="relative flex flex-wrap gap-2 mt-3">
                <span className="text-[11px] font-bold text-slate-600 bg-slate-100 rounded-full px-2.5 py-1">{featuredQuiz.question_count} {t("quiz.questions", "întrebări")}</span>
                <span
                  className="text-[11px] font-bold text-amber-700 bg-amber-100 rounded-full px-2.5 py-1"
                  title={t("quiz.points_bonus_hint", "Bonus de viteză și scor perfect")}
                >
                  {t("quiz.points_plus_bonus", "{{points}} puncte + bonus", { points: featuredQuiz.max_points ?? featuredQuiz.reward_points })}
                </span>
              </div>
              <div className="relative mt-4 flex items-center justify-center gap-2 bg-gradient-to-br from-indigo-500 to-blue-600 text-white font-extrabold text-sm py-3 rounded-2xl shadow-[0_4px_0_#3730a3] active:translate-y-[2px] transition-transform">
                {t("quiz.start", "Începe")}
                <GameIcon name="bolt" className="w-4 h-4" />
              </div>
            </GameCard>
          </div>
        )}

        {/* Următorul antrenament */}
        {nextSession && (
          <div>
            <div className="flex items-center justify-between mb-2.5 px-0.5">
              <h3 className="text-xs font-extrabold uppercase tracking-wide text-slate-400">{t("dashboard.nextSession", "Următorul antrenament")}</h3>
              <Link to="/app/sessions" className="text-xs font-bold text-indigo-600">{t("common.viewAll", "Vezi toate")}</Link>
            </div>
            <GameCard depth onClick={() => navigate("/app/sessions")} className="flex items-center gap-3 p-4 cursor-pointer">
              <div className="w-12 h-12 rounded-2xl bg-blue-100 flex flex-col items-center justify-center text-blue-700 shrink-0">
                <span className="text-base font-black leading-none">{new Date(nextSession.start_at).getDate()}</span>
                <span className="text-[9px] font-bold uppercase">{new Date(nextSession.start_at).toLocaleString("ro-RO", { month: "short" })}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-extrabold text-slate-800 truncate">{nextSession.title}</div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">{nextSession.location_text || "—"}</div>
              </div>
              <div className="text-sm font-extrabold text-indigo-600 shrink-0">
                {new Date(nextSession.start_at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}
              </div>
            </GameCard>
          </div>
        )}

        {/* Insigne recente */}
        {badgeStrip.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2.5 px-0.5">
              <h3 className="text-xs font-extrabold uppercase tracking-wide text-slate-400">{t("dashboard.recentBadges", "Insigne recente")}</h3>
              <Link to="/app/badges" className="text-xs font-bold text-indigo-600">{t("common.viewAll", "Vezi toate")}</Link>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-1 -mx-4 px-4 [&::-webkit-scrollbar]:hidden">
              {badgeStrip.map((b) => (
                <button key={b.id} onClick={() => navigate("/app/badges")} className="shrink-0 w-16 text-center">
                  <div className={`w-14 h-14 mx-auto mb-1.5 rounded-full grid place-items-center text-2xl bg-white border border-slate-100 shadow-[0_3px_0_#eef2f8] ${b.earned ? "" : "grayscale opacity-40"}`}>
                    {b.icon_image_url ? (
                      <img src={b.icon_image_url} alt={b.name} className="w-full h-full object-contain p-1.5" />
                    ) : (
                      <span>{b.icon_emoji || "🏅"}</span>
                    )}
                  </div>
                  <div className="text-[9.5px] font-bold text-slate-500 leading-tight">{b.name}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Clasament club — hidden for individual accounts (the "club" is the
            shared placeholder pool, which isn't a meaningful ranking). */}
        {!individual && leaderPeek.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2.5 px-0.5">
              <h3 className="text-xs font-extrabold uppercase tracking-wide text-slate-400">{t("dashboard.clubLeaderboard", "Clasament club")}</h3>
              <Link to="/app/leaderboard" className="text-xs font-bold text-indigo-600">{t("common.viewAll", "Vezi tot")}</Link>
            </div>
            <GameCard depth className="p-2">
              {leaderPeek.map((e) => {
                const isMe = e.user_id === user?.id;
                return (
                  <div key={e.user_id} className={`flex items-center gap-3 px-3 py-2.5 rounded-2xl ${isMe ? "bg-indigo-50 border border-indigo-100" : ""}`}>
                    <span className={`w-6 text-center font-black text-sm ${e.rank <= 3 ? "text-amber-500" : "text-slate-400"}`}>{e.rank}</span>
                    {e.avatar_url ? (
                      <img src={e.avatar_url} alt={e.full_name} className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <span className="w-8 h-8 rounded-full grid place-items-center text-xs font-extrabold text-white bg-gradient-to-br from-indigo-500 to-blue-600">{getInitials(e.full_name)}</span>
                    )}
                    <span className="flex-1 min-w-0 truncate text-sm font-bold text-slate-700">{isMe ? `${t("leaderboard.you", "Tu")} · ${e.full_name}` : e.full_name}</span>
                    <span className="inline-flex items-center gap-1 text-sm font-extrabold text-amber-600">
                      <GameIcon name="star" className="w-3.5 h-3.5" />
                      {e.points.toLocaleString("ro-RO")}
                    </span>
                  </div>
                );
              })}
            </GameCard>
          </div>
        )}

        {/* My Info Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800">{t("dashboard.myInfo", "Informațiile mele")}</h2>
          </div>
          <div className="p-4 space-y-4">
            {/* Club */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-500">{t("dashboard.club", "Club")}</p>
                <p className="font-medium text-slate-800 truncate">
                  {individual ? t("dashboard.individualAccount", "Cont individual") : currentClub?.name || "-"}
                </p>
              </div>
            </div>

            {/* Sports */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-500">{t("dashboard.sports", "Sport")}</p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {sports.length > 0 ? sports.map((sport, idx) => (
                    <span key={idx} className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                      {sport}
                    </span>
                  )) : (
                    <span className="text-slate-500 text-sm">{individual ? t("dashboard.notSetYet", "Nesetat încă") : "-"}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Teams */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-500">{t("dashboard.teams", "Echipe")}</p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {myTeams.length > 0 ? myTeams.map((team) => (
                    <span key={team.id} className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                      {team.name}
                    </span>
                  )) : (
                    <span className="text-slate-500 text-sm">{individual ? t("dashboard.noTeam", "Fără echipă") : "-"}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/*
          A child's home screen is not the place for a debt.

          The amounts, the red "Întârziat" and the due dates used to sit here
          for every athlete, including a ten-year-old who cannot pay and cannot
          do anything about it but worry. They now show only for an athlete who
          IS the payer; a child gets one line asking them to tell their parent,
          which is the only action actually available to them.
        */}
        {myProducts.length > 0 && !payerIsSelf && myProducts.some((p) => p.payment_status !== "paid") && (
          <div className="p-4 bg-white border border-indigo-100 rounded-2xl">
            <p className="text-sm font-bold text-slate-800">
              {t("dashboard.tellParentTitle", "Amintește-i părintelui de cotizație")}
            </p>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">
              {t(
                "dashboard.tellParentBody",
                "Clubul așteaptă plata cotizației. Spune-i părintelui tău — el o achită din aplicația lui."
              )}
            </p>
          </div>
        )}

        {/* My Services / Payments */}
        {myProducts.length > 0 && payerIsSelf && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-100 flex items-center justify-between">
              <h2 className="font-semibold text-emerald-800">{t("dashboard.myServices", "Serviciile mele")}</h2>
              <Link to="/app/payments" className="text-sm text-emerald-600 font-medium">
                {t("common.viewAll", "Vezi toate")}
              </Link>
            </div>
            <div className="p-4 space-y-3">
              {myProducts.map((ap) => {
                const isPending = ap.payment_status === "pending";
                const isOverdue = ap.payment_status === "overdue";
                const isPaid = ap.payment_status === "paid";
                const price = (ap.product.price_cents / 100).toFixed(2);
                const currency = ap.product.currency || "RON";

                return (
                  <div key={ap.id} className={`p-3 rounded-xl border ${
                    isOverdue ? "bg-red-50 border-red-200" :
                    isPending ? "bg-amber-50 border-amber-200" :
                    "bg-emerald-50 border-emerald-200"
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-slate-800">{ap.product.name}</h4>
                        {ap.product.description && (
                          <p className="text-xs text-slate-500 mt-0.5">{ap.product.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-sm font-semibold text-slate-800">{price} {currency}</span>
                          <span className="text-xs text-slate-500">/ {
                            ap.product.recurrence_type === "monthly" ? t("common.month", "lună") :
                            ap.product.recurrence_type === "weekly" ? t("common.week", "săptămână") :
                            t("common.hour", "oră")
                          }</span>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        isOverdue ? "bg-red-100 text-red-700" :
                        isPending ? "bg-amber-100 text-amber-700" :
                        "bg-emerald-100 text-emerald-700"
                      }`}>
                        {isOverdue ? t("payment.overdue", "Întârziat") :
                         isPending ? t("payment.pending", "De plată") :
                         t("payment.paid", "Plătit")}
                      </span>
                    </div>

                    {/* Due Date */}
                    {ap.next_due_date && !isPaid && (
                      <div className="flex items-center gap-2 mt-2 text-sm">
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className={isOverdue ? "text-red-600 font-medium" : "text-slate-600"}>
                          {t("payment.dueDate", "Scadență")}: {formatDate(new Date(ap.next_due_date))}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Payment Methods */}
              {clubPaymentMethods.length > 0 && myProducts.some(p => p.payment_status !== "paid") && (
                <div className="mt-4 p-3 bg-slate-50 rounded-xl">
                  <h5 className="text-sm font-medium text-slate-700 mb-2">{t("payment.acceptedMethods", "Metode de plată acceptate")}:</h5>
                  <div className="flex flex-wrap gap-2">
                    {clubPaymentMethods.includes("cash") && (
                      <span className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-xs text-slate-600">
                        <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        {t("payment.cash", "Numerar")}
                      </span>
                    )}
                    {clubPaymentMethods.includes("card") && (
                      <span className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-xs text-slate-600">
                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                        {t("payment.card", "Card")}
                      </span>
                    )}
                    {clubPaymentMethods.includes("bank_transfer") && (
                      <span className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-xs text-slate-600">
                        <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                        </svg>
                        {t("payment.bankTransfer", "Transfer bancar")}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
      </div>
    </MobileAppLayout>
  );
}
